import { useMemo, useState } from "react"
import YearBarChart from "components/molecules/charts/YearBarChart"
import { ageDistributionByYear } from "data/ageDistribution"
import useTranslations from "hooks/useTranslations"

/**
 * The annual report's age histogram, on the UK schools page.
 *
 * Ellinora and Kaisa asked for the report's chart here — "so that the histogram
 * would work like in the annual report", i.e. interactive, with the year
 * picker, not a screenshot.
 *
 * ⚠️ **Almost every word on this section is the report's own copy.** It reads
 * the same `report2025` translations the report page reads
 * (`ageDistributionHeading`, `ageDistributionText2`, the chart's axis and
 * tooltip labels) and the same `data/ageDistribution` figures. Change those in
 * `config/translations/*.json` under `report2025` and both pages move together.
 *
 * That is deliberate. The paragraphs make specific claims about SomeBuddy's
 * casework, and those have been through review for the report. Writing fresh
 * marketing prose around the same chart would put unreviewed claims on a sales
 * page.
 *
 * The one exception is the first paragraph, which comes from the page's own
 * `ukSchoolsAgeDistributionText` instead of `report2025.ageDistributionText`.
 * Ellinora asked for its opening sentence to be reworded for this page — "but
 * this change should be on this page only, not in the annual report" — so the
 * override is exactly one key wide. The rest of that paragraph is still the
 * report's sentences, copied across verbatim; keep it that way, and keep any
 * further wording change scoped to this key unless the report is meant to move
 * with it.
 *
 * Two things differ from the report, both forced by where the section sits:
 *
 * - **Light, not dark.** It goes between the mint carousel and Testimonials'
 *   `bg-zinc-800`, and the page's rule is that no two adjacent bands share a
 *   ground, so white is the only option left. The bars keep the report's
 *   greens, which read fine on white.
 * - **English percentages.** Finnish sets a space before the percent sign, so
 *   the report renders "13,5 %". Here it is "13.5%".
 */
export default function UkAgeDistribution() {
  const translations = useTranslations()
  const report = (translations?.report2025 as Record<string, string>) || {}

  // This page's own opening paragraph — the single deliberate departure from
  // the report's copy. Falls back to the report's if the key is ever missing,
  // so the section never renders a gap where a paragraph should be.
  const ukSchoolsAgeDistributionText =
    translations?.ukSchoolsAgeDistributionText

  const {
    ageDistributionHeading,
    ageDistributionText,
    ageDistributionText2,
    ageDistributionText2Highlight,
    ageChartTitle,
    ageChartShowYearAriaPrefix,
    ageChartTooltipLabel,
    ageChartTooltipValueLabel,
    ageChartXAxisLabel,
    ageChartYAxisLabel,
  } = report

  const years = useMemo(() => Object.keys(ageDistributionByYear).sort(), [])
  const [year, setYear] = useState(years[years.length - 1])
  const data = ageDistributionByYear[year] ?? []

  // Same split the report uses to bold one phrase inside a paragraph.
  const secondParagraph = (() => {
    const text = ageDistributionText2
    const phrase = ageDistributionText2Highlight
    if (!phrase || typeof text !== "string" || !text.includes(phrase)) {
      return text
    }
    const [before, ...rest] = text.split(phrase)
    return (
      <>
        {before}
        <span className="font-semibold text-zinc-900">{phrase}</span>
        {rest.join(phrase)}
      </>
    )
  })()

  return (
    <div className="bg-white py-16 lg:py-24">
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-y-12 lg:grid-cols-2 lg:gap-x-16">
          {/* Copy column. The heading is never wrapped in ScrollAnimate — it
              starts at opacity 0 and would read as a broken page on load. */}
          <div>
            <h2 className="m-0 max-w-xl font-display text-3xl font-extrabold leading-tight tracking-tight text-zinc-900 lg:text-4xl">
              {ageDistributionHeading}
            </h2>
            <p className="m-0 mt-8 max-w-xl text-lg leading-relaxed text-zinc-700">
              {ukSchoolsAgeDistributionText || ageDistributionText}
            </p>
            <p className="m-0 mt-4 max-w-xl text-lg leading-relaxed text-zinc-700">
              {secondParagraph}
            </p>
          </div>

          {/* Chart column */}
          <div className="lg:pl-4">
            <p className="m-0 text-base font-semibold text-zinc-700">
              {ageChartTitle}
            </p>

            <div className="mb-4 mt-3 flex flex-wrap gap-1.5">
              {years.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setYear(option)}
                  className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                    year === option
                      ? "bg-kkm-darkgreen text-white"
                      : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                  }`}
                  aria-pressed={year === option}
                  aria-label={`${ageChartShowYearAriaPrefix} ${option}`}
                >
                  {option}
                </button>
              ))}
            </div>

            <div className="h-80 w-full lg:h-96">
              <YearBarChart
                chartData={data}
                xDataKey="age"
                valueKey="cases"
                valueSuffix="%"
                percentSuffix="%"
                tooltipLabel={ageChartTooltipLabel}
                tooltipValueLabel={ageChartTooltipValueLabel}
                xAxisLabel={ageChartXAxisLabel}
                yAxisLabel={ageChartYAxisLabel}
              />
            </div>

            {/* Recharts draws to an unlabelled SVG, so the same numbers are
                published as a real table for screen readers. It is rebuilt from
                `data`, so it always agrees with the bars, and its labels are the
                chart's own so it introduces no new copy.

                ⚠️ `sr-only` goes on this wrapper and never on the <table>
                itself. It hides things with `width: 1px` + `overflow: hidden`,
                and a table under `table-layout: auto` sizes to its content and
                ignores that width — the "hidden" table then renders at full
                width, overflowing the viewport on mobile and leaving a white
                gutter down the right of every full-bleed section on the page. */}
            <div className="sr-only">
              <table>
                <caption>{`${ageChartTitle} ${year}`}</caption>
                <thead>
                  <tr>
                    <th scope="col">{ageChartXAxisLabel}</th>
                    <th scope="col">{ageChartYAxisLabel}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => (
                    <tr key={row.age}>
                      <th scope="row">{row.age}</th>
                      <td>{row.cases.toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
